/**
 * Banner shown on the cloud Landing when localStorage holds projects
 * the user hasn't imported yet. Each row offers a one-click import to
 * the active org via /v1/orgs/:slug/projects/import; the original
 * localStorage entry is left in place so the user can verify the
 * import before deleting it themselves.
 *
 * Dismiss-all writes a flag to localStorage so the banner doesn't
 * reappear on the next visit. The flag is keyed per-org to keep
 * multi-org users in control.
 */

import { useEffect, useState } from 'react';

import { orgApi } from '../data/orgApi.js';
import { listProjects, type StoredProject } from '../projects.js';

import './BringYourProjects.css';

const DISMISSED_KEY_PREFIX = 'bliksys.bringYourProjects.dismissed.';

interface Props {
  orgSlug: string;
  /** Called after a successful import so the parent can refresh its
   * project list. */
  onImported: () => void;
}

interface Row {
  project: StoredProject;
  status: 'idle' | 'importing' | 'imported' | 'error';
  message?: string;
}

export function BringYourProjects({ orgSlug, onImported }: Props): JSX.Element | null {
  const dismissedKey = `${DISMISSED_KEY_PREFIX}${orgSlug}`;
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(dismissedKey));
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (dismissed) return;
    const local = listProjects();
    setRows(local.map((project) => ({ project, status: 'idle' })));
  }, [dismissed]);

  if (dismissed) return null;
  if (rows.length === 0) return null;

  const allDone = rows.every((r) => r.status === 'imported');

  const api = orgApi({ orgSlug });

  const handleImport = async (id: string): Promise<void> => {
    setRows((rs) =>
      rs.map((r) => {
        if (r.project.id !== id) return r;
        const { message: _drop, ...rest } = r;
        return { ...rest, status: 'importing' };
      }),
    );
    const row = rows.find((r) => r.project.id === id);
    if (!row) return;
    try {
      await api.importProject({
        name: row.project.name,
        schema: row.project.schema,
        data: row.project.data,
      });
      setRows((rs) => rs.map((r) => (r.project.id === id ? { ...r, status: 'imported' } : r)));
      onImported();
    } catch (err) {
      setRows((rs) =>
        rs.map((r) =>
          r.project.id === id
            ? {
                ...r,
                status: 'error',
                message: err instanceof Error ? err.message : 'Import failed.',
              }
            : r,
        ),
      );
    }
  };

  const handleDismiss = (): void => {
    try {
      window.localStorage.setItem(dismissedKey, '1');
    } catch {
      /* private mode — banner just keeps reappearing; not fatal. */
    }
    setDismissed(true);
  };

  return (
    <section className="bring-your-projects" aria-labelledby="byp-heading">
      <header className="bring-your-projects-header">
        <h2 id="byp-heading" className="bring-your-projects-title">
          {allDone ? 'Imported!' : 'Bring your projects'}
        </h2>
        <p className="bring-your-projects-subtitle">
          {allDone
            ? `All ${String(rows.length)} local project(s) imported. You can dismiss this notice.`
            : `We found ${String(rows.length)} project(s) saved in this browser. Import them to
              your organization (${orgSlug}) so they're shared across devices.`}
        </p>
      </header>

      <ul className="bring-your-projects-list">
        {rows.map((row) => (
          <li key={row.project.id} className="bring-your-projects-row">
            <div className="bring-your-projects-meta">
              <strong>{row.project.name}</strong>
              <span className="bring-your-projects-time">
                Updated {new Date(row.project.updatedAt).toLocaleString()}
              </span>
            </div>
            {row.status === 'imported' ? (
              <span className="bring-your-projects-status">Imported ✓</span>
            ) : row.status === 'importing' ? (
              <span className="bring-your-projects-status">Importing…</span>
            ) : (
              <button
                type="button"
                className="bring-your-projects-import"
                onClick={() => void handleImport(row.project.id)}
              >
                Import
              </button>
            )}
            {row.status === 'error' && row.message && (
              <span className="bring-your-projects-error" role="alert">
                {row.message}
              </span>
            )}
          </li>
        ))}
      </ul>

      <footer className="bring-your-projects-footer">
        <button type="button" className="bring-your-projects-dismiss" onClick={handleDismiss}>
          {allDone ? 'Hide' : 'Dismiss'}
        </button>
      </footer>
    </section>
  );
}

function readDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
